import {
  Loader2,
  Lock,
  RotateCcw,
  SearchX,
  ServerCrash,
  TriangleAlert,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { StateCard } from '@/components/common/StateCard';
import type { StateMessageSize } from '@/components/common/StateMessage';
import { Button } from '@/components/ui/button';
import type { StatusColorTone } from '@/styles/statusColors';
import { resolveApiErrorDisplay, type ApiErrorKind } from '@/utils/errors';

interface KindStyle {
  icon: LucideIcon;
  tone: StatusColorTone;
  canRetry: boolean;
  showStatus: boolean;
}

const KIND_STYLE: Record<ApiErrorKind, KindStyle> = {
  offline: { icon: WifiOff, tone: 'warning', canRetry: true, showStatus: false },
  unreachable: { icon: ServerCrash, tone: 'danger', canRetry: true, showStatus: false },
  server: { icon: ServerCrash, tone: 'danger', canRetry: true, showStatus: true },
  'access-denied': { icon: Lock, tone: 'neutral', canRetry: false, showStatus: false },
  'not-found': { icon: SearchX, tone: 'neutral', canRetry: false, showStatus: false },
  request: { icon: TriangleAlert, tone: 'warning', canRetry: true, showStatus: true },
};

interface ErrorStateProps {
  error: unknown;
  fallbackTitle: string;
  onRetry?: (() => void) | undefined;
  isRetrying?: boolean | undefined;
  action?: ReactNode;
  size?: StateMessageSize | undefined;
  fill?: boolean | undefined;
  className?: string | undefined;
}

export function ErrorState({
  error,
  fallbackTitle,
  onRetry,
  isRetrying = false,
  action,
  size,
  fill = false,
  className,
}: ErrorStateProps) {
  const { kind, title, description, status } = resolveApiErrorDisplay(error, fallbackTitle);
  const { icon, tone, canRetry, showStatus } = KIND_STYLE[kind];
  const showRetry = canRetry && onRetry !== undefined;

  return (
    <StateCard
      icon={icon}
      tone={tone}
      title={title}
      description={description}
      size={size}
      fill={fill}
      className={className}
      {...(showStatus && status !== undefined && { note: `Error code ${status}` })}
      {...((showRetry || action) && {
        actions: (
          <>
            {showRetry && (
              <Button size="sm" onClick={onRetry} disabled={isRetrying} className="gap-1.5">
                {isRetrying ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="size-3.5" />
                )}
                {isRetrying ? 'Retrying...' : 'Try again'}
              </Button>
            )}
            {action}
          </>
        ),
      })}
    />
  );
}
